'use client';

import { ChevronRight, Users } from 'lucide-react';
import type { Person } from '@/lib/types';
import { useProfile } from './ProfileContext';
import { formerElsewhere, household, relationOf } from './relations';
import { FactCard } from './ui';
import { ElsewhereLine, RelationRow } from '@church/nextjs-ui/website/profile';

/**
 * The Family card: who this person's people are, beside the About card that
 * says who they are. Tapping the card (or its Edit) opens the whole Family
 * page; tapping a household name opens the picker for that one relationship.
 */
export function FamilyCard({
  person,
  onManage,
  onRelate,
}: {
  person: Person;
  onManage: () => void;
  onRelate: (other: Person) => void;
}) {
  return (
    <FactCard
      title="Family"
      icon={<Users className="h-4 w-4" strokeWidth={2.5} />}
      onEdit={onManage}
    >
      <FamilyRows person={person} onManage={onManage} onRelate={onRelate} />
    </FactCard>
  );
}

/**
 * The Family card's body: everyone else in the household with what they are
 * to this person ("Rachel · Mother"), or "Not set" as a tap that opens the
 * picker straight away; then relatives elsewhere; and requests waiting on
 * this person that you can answer (yours, or, as a head of household,
 * anyone's in the house).
 */
function FamilyRows({
  person,
  onManage,
  onRelate,
}: {
  person: Person;
  onManage: () => void;
  /** Open the picker for "`other` is … of this person" */
  onRelate: (other: Person) => void;
}) {
  const { data, requests } = useProfile();
  const isMe = person.contactId === data.me.contactId;
  const others = household(data).filter((p) => p.contactId !== person.contactId);
  const formerly = formerElsewhere(data, person);
  const first = person.nickname || person.firstName;
  // A head of household is sent everyone's in the house; each card shows its own.
  const incoming =
    requests && requests !== 'error'
      ? requests.incoming.filter((r) => r.toContactId === person.contactId).length
      : 0;
  const outgoing = isMe && requests && requests !== 'error' ? requests.outgoing.length : 0;
  const unset = others.filter((o) => !relationOf(person, o.contactId)).length;

  return (
    <div className="flex flex-col gap-3">
      {incoming > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onManage();
          }}
          className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#299c8f] px-3 py-1 text-xs font-bold text-white transition-all hover:scale-105 active:scale-95"
        >
          {isMe
            ? incoming === 1
              ? 'A request is waiting on you'
              : `${incoming} requests are waiting on you`
            : incoming === 1
              ? `A request for ${first} is waiting`
              : `${incoming} requests for ${first} are waiting`}
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={3} />
        </button>
      )}
      {others.length > 0 && (
        <ul className="-mx-2 flex flex-col">
          {others.map((o) => {
            const rel = relationOf(person, o.contactId);
            return (
              <li key={o.contactId}>
                <RelationRow
                  name={o.displayName}
                  label={o.contactId === data.me.contactId ? 'You' : o.nickname || o.firstName}
                  imageUrl={o.imageUrl}
                  relation={rel ? rel.label : null}
                  onClick={() => onRelate(o)}
                />
              </li>
            );
          })}
        </ul>
      )}
      {(person.family.length > 0 || formerly.length > 0 || outgoing > 0) && (
        <ElsewhereLine
          people={[
            ...person.family.map((f) => ({
              key: f.contactRelationshipId,
              name: f.displayName,
              imageUrl: f.imageUrl,
            })),
            ...formerly.map((f) => ({ key: -f.contactId, name: f.name, imageUrl: f.imageUrl })),
          ]}
        >
          {[
            elsewhereSummary([
              // Relationships first; then those who lived here with none.
              ...person.family.map((f) => `${f.displayName.split(' ')[0]} (${f.label})`),
              ...formerly.map((f) => `${f.firstName || f.name.split(' ')[0]} (lived here)`),
            ]),
            outgoing > 0 ? `${outgoing} waiting on them` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </ElsewhereLine>
      )}
      {others.length === 0 && person.family.length === 0 && formerly.length === 0 && (
        <p className="text-sm text-gray-500">
          {isMe
            ? 'Nobody linked yet. Tap this card to add people who live elsewhere.'
            : `Nobody linked to ${first} yet.`}
        </p>
      )}
      {unset > 0 && others.length > 0 && (
        <p className="text-xs text-gray-500">
          Tap a name to say how {isMe ? 'you are' : `${first} is`} related.
        </p>
      )}
    </div>
  );
}

/** "Elsewhere: Tonya (Mother), Art (Father), Arizona (lived here) +2", or null. */
function elsewhereSummary(names: string[]): string | null {
  if (names.length === 0) return null;
  const more = names.length > 3 ? ` +${names.length - 3}` : '';
  return `Elsewhere: ${names.slice(0, 3).join(', ')}${more}`;
}
